import React, { useState, useEffect, useCallback } from 'react';

// Painel reutilizável de lançamentos financeiros vinculados a uma entidade.
// Usado no cadastro do FORNECEDOR (tipo="pagar") e do CLIENTE (tipo="receber").
// Cada parcela é um registro do financeiro (fonte única de verdade): aparece
// na tela Financeiro e alterar o valor aqui reflete lá (e vice-versa).
const token = () => localStorage.getItem('token');
const apiGet = (p) => fetch(`/${p}${p.includes('?') ? '&' : '?'}_=${Date.now()}`, { headers: { Authorization: `Bearer ${token()}`, 'Cache-Control': 'no-cache' } }).then(r => r.json());
const apiSend = (p, m, b) => fetch(`/${p}`, { method: m, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` }, body: b ? JSON.stringify(b) : undefined }).then(r => r.json());

const inp = { padding: '4px 8px', borderRadius: 3, border: '1px solid #b0b8c4', fontSize: 13, color: '#111', background: '#fff', height: 30, outline: 'none', width: '100%', boxSizing: 'border-box' };
const lbl = { fontSize: 11, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 2 };
const moeda = (v) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtData = (iso) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || ''); return m ? `${m[3]}/${m[2]}/${m[1]}` : '—'; };
const mascaraMoeda = (v) => {
  const n = String(v).replace(/\D/g, '');
  if (!n) return '';
  const val = (parseInt(n, 10) / 100).toFixed(2);
  const [i, d] = val.split('.');
  return i.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + d;
};

export default function PainelLancamentos({ tipo, idField, idValue, nomeValue }) {
  // Consultor (não-admin) pode ver e lançar; baixa, edição e exclusão são do Vagner
  const ehAdmin = localStorage.getItem('nomeUsuario') === 'Vagner Freitas';
  const rec = tipo === 'receber';
  const nomeField = idField === 'fornecedor_id' ? 'fornecedor_nome' : 'cliente_nome';
  const rotuloEntidade = idField === 'fornecedor_id' ? 'fornecedor' : 'cliente';
  const telaAlvo = rec ? 'Contas a Receber' : 'Contas a Pagar';

  const [contas, setContas] = useState([]);
  const [cats, setCats] = useState([]);
  const [formas, setFormas] = useState([]);
  const [nova, setNova] = useState({ descricao: '', valor_total: '', num_parcelas: 1, data_primeira: '', categoria: '', subcategoria: '', forma_pagamento: '' });
  const [salvando, setSalvando] = useState(false);

  // Venda multi-produto (a receber): itens do catálogo de serviços, cada um
  // com quantidade e valor — UM contas a receber do total (igual ao Gravar
  // Cliente do CRM)
  const ITEM_VAZIO = { servico: '', quantidade: 1, valor: '' };
  const [itens, setItens] = useState([{ ...ITEM_VAZIO }]);
  const [catalogo, setCatalogo] = useState([]);

  const carregar = useCallback(async () => {
    const d = await apiGet(`financeiro?${idField}=${idValue}&tipo=${tipo}`);
    setContas(d?.itens || []);
    setCats(rec ? (d?.categorias?.receber || []) : (d?.categorias?.pagar || []));
    setFormas(d?.formas || []);
  }, [idValue, idField, tipo, rec]);
  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    if (!rec) return;
    apiGet('oportunidades/servicos-catalogo')
      .then(d => setCatalogo((d.servicos || []).map(s => s.nome).filter(Boolean)))
      .catch(() => {});
  }, [rec]);

  const subs = (cats.find(c => c.nome === nova.categoria)?.subs) || [];
  const setN = (k) => (e) => setNova({ ...nova, [k]: e.target.value });

  const setItem = (i, campo, valor) => setItens(arr => arr.map((it, k) => k === i ? { ...it, [campo]: valor } : it));
  const parseMoeda = (v) => parseFloat(String(v || '').replace(/\./g, '').replace(',', '.')) || 0;
  // Valor do item é UNITÁRIO: total da linha = quantidade × valor
  const totalItens = itens.reduce((s, it) => s + (Math.max(1, parseInt(it.quantidade, 10) || 1)) * parseMoeda(it.valor), 0);

  const lancar = async () => {
    if (rec) {
      const validos = itens.filter(it => it.servico && parseMoeda(it.valor) > 0);
      if (!validos.length) return alert('Escolha o serviço e informe o valor de pelo menos um produto.');
      setSalvando(true);
      await apiSend('financeiro/parcelado', 'POST', {
        tipo, itens: validos,
        num_parcelas: nova.num_parcelas, data_primeira: nova.data_primeira, forma_pagamento: nova.forma_pagamento,
        [idField]: idValue, [nomeField]: nomeValue,
      });
      setItens([{ ...ITEM_VAZIO }]);
    } else {
      if (!nova.descricao.trim()) return alert('Informe a descrição.');
      if (!nova.valor_total) return alert('Informe o valor total.');
      setSalvando(true);
      await apiSend('financeiro/parcelado', 'POST', { tipo, ...nova, [idField]: idValue, [nomeField]: nomeValue });
    }
    setNova({ descricao: '', valor_total: '', num_parcelas: 1, data_primeira: '', categoria: '', subcategoria: '', forma_pagamento: '' });
    setSalvando(false);
    carregar();
  };

  const editarValor = async (it, novo) => {
    const atual = (it.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (novo === atual) return;
    await apiSend(`financeiro/${it.id}`, 'PUT', { valor: novo });
    carregar();
  };

  // Parcelamento nasce mês a mês, mas na prática a 2ª parcela às vezes cai
  // dentro do mesmo mês (entrada + saldo em 15 dias, p.ex.) — aqui a data de
  // cada parcela é ajustável e reflete no Financeiro, igual ao valor.
  const editarVencimento = async (it, novo) => {
    if (!novo || novo === (it.vencimento || '').slice(0, 10)) return;
    await apiSend(`financeiro/${it.id}`, 'PUT', { vencimento: novo });
    carregar();
  };
  const excluir = async (it) => { if (!window.confirm('Excluir esta parcela?')) return; await apiSend(`financeiro/${it.id}`, 'DELETE'); carregar(); };

  // Relatório do pedido em PDF: junta itens + parcelas do mesmo grupo,
  // pronto pra mandar pro cliente na prestação de contas
  const [gerandoPdf, setGerandoPdf] = useState('');
  const baixarPedido = async (it) => {
    setGerandoPdf(it.grupo_parcelas);
    try {
      const res = await fetch(`/financeiro/pedido/${encodeURIComponent(it.grupo_parcelas)}/pdf`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.erro || 'Erro ao gerar o relatório');
      }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `relatorio_pedido_${(nomeValue || 'cliente').toLowerCase().replace(/[^a-z0-9]+/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (e) { alert(e.message); }
    setGerandoPdf('');
  };
  const [baixa, setBaixa] = useState(null);
  const [editarVenda, setEditarVenda] = useState('');
  const [trocaConsultor, setTrocaConsultor] = useState(null); // { grupo, atual }

  const totalAberto = contas.filter(c => c.status === 'aberto').reduce((s, c) => s + c.valor, 0);

  return (
    <div>
      {baixa && <ModalBaixa item={baixa} formas={formas} onFechar={() => setBaixa(null)} onSalvo={() => { setBaixa(null); carregar(); }} />}
      {editarVenda && <ModalEditarVenda grupo={editarVenda} catalogo={catalogo} formas={formas} onFechar={() => setEditarVenda('')} onSalvo={() => { setEditarVenda(''); carregar(); }} />}
      {trocaConsultor && <ModalConsultor grupo={trocaConsultor.grupo} atual={trocaConsultor.atual} onFechar={() => setTrocaConsultor(null)} onSalvo={() => { setTrocaConsultor(null); carregar(); }} />}

      <div style={{ background: '#f5f7fa', border: '1px solid #e2e8f0', borderRadius: 6, padding: 12, marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0a1628', marginBottom: 8 }}>{rec ? 'Lançar venda (um pagamento, um ou mais produtos)' : 'Lançar conta a pagar'}</div>

        {rec ? (
          <>
            {/* Produtos da venda — mesmo modelo do Gravar Cliente do CRM */}
            {itens.map((it, i) => (
              <div key={i} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 6, alignItems: 'flex-end' }}>
                <div style={{ flex: 3, minWidth: 180 }}>
                  <label style={lbl}>Serviço vendido</label>
                  <select value={it.servico} onChange={e => setItem(i, 'servico', e.target.value)} style={inp}>
                    <option value="">— escolha o serviço —</option>
                    {catalogo.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div style={{ width: 70 }}>
                  <label style={lbl}>Qtd</label>
                  <input type="number" min={1} value={it.quantidade} onChange={e => setItem(i, 'quantidade', Math.max(1, parseInt(e.target.value, 10) || 1))} style={inp} />
                </div>
                <div style={{ flex: 1, minWidth: 110 }}>
                  <label style={lbl}>Valor unitário (R$)</label>
                  <input value={it.valor} onChange={e => setItem(i, 'valor', mascaraMoeda(e.target.value))} inputMode="numeric" placeholder="0,00" style={inp} />
                </div>
                {parseInt(it.quantidade, 10) > 1 && parseMoeda(it.valor) > 0 && (
                  <div style={{ fontSize: 11.5, color: '#0a1628', fontWeight: 600, paddingBottom: 7 }}>= {moeda(parseInt(it.quantidade, 10) * parseMoeda(it.valor))}</div>
                )}
                {itens.length > 1 && (
                  <button onClick={() => setItens(arr => arr.filter((_, k) => k !== i))} title="Remover produto"
                    style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: 4, height: 30, padding: '0 10px', cursor: 'pointer', fontWeight: 700 }}>✕</button>
                )}
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <button onClick={() => setItens(arr => [...arr, { ...ITEM_VAZIO }])}
                style={{ background: '#fff', color: '#0a1628', border: '1px dashed #94a3b8', borderRadius: 4, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                ➕ Adicionar produto
              </button>
              {totalItens > 0 && <span style={{ fontSize: 12.5, color: '#0a1628' }}>Total da venda: <b>{moeda(totalItens)}</b></span>}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>
              <div style={{ flex: 1, minWidth: 80 }}><label style={lbl}>Parcelas</label>
                <select value={nova.num_parcelas} onChange={setN('num_parcelas')} style={inp}>{Array.from({ length: 24 }, (_, k) => k + 1).map(n => <option key={n} value={n}>{n}x</option>)}</select>
              </div>
              <div style={{ flex: 1, minWidth: 120 }}><label style={lbl}>1º vencimento</label><input type="date" value={nova.data_primeira} onChange={setN('data_primeira')} style={inp} /></div>
              <div style={{ flex: 1, minWidth: 110 }}><label style={lbl}>Forma</label>
                <select value={nova.forma_pagamento} onChange={setN('forma_pagamento')} style={inp}><option value="">—</option>{formas.map(x => <option key={x} value={x}>{x}</option>)}</select>
              </div>
              <button onClick={lancar} disabled={salvando} style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 4, padding: '0 18px', height: 30, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: salvando ? 0.6 : 1 }}>{salvando ? '...' : 'Lançar venda'}</button>
            </div>
            {totalItens > 0 && nova.num_parcelas > 1 && (
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>{nova.num_parcelas}x de aprox. {moeda(totalItens / nova.num_parcelas)} · um único contas a receber com todos os produtos</div>
            )}
          </>
        ) : (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <div style={{ flex: 3, minWidth: 180 }}><label style={lbl}>Descrição</label><input value={nova.descricao} onChange={setN('descricao')} placeholder="Ex: Compra de material" style={inp} /></div>
              <div style={{ flex: 1, minWidth: 100 }}><label style={lbl}>Valor total (R$)</label><input value={nova.valor_total} onChange={e => setNova({ ...nova, valor_total: mascaraMoeda(e.target.value) })} inputMode="numeric" placeholder="0,00" style={inp} /></div>
              <div style={{ flex: 1, minWidth: 80 }}><label style={lbl}>Parcelas</label>
                <select value={nova.num_parcelas} onChange={setN('num_parcelas')} style={inp}>{Array.from({ length: 24 }, (_, k) => k + 1).map(n => <option key={n} value={n}>{n}x</option>)}</select>
              </div>
              <div style={{ flex: 1, minWidth: 120 }}><label style={lbl}>1º vencimento</label><input type="date" value={nova.data_primeira} onChange={setN('data_primeira')} style={inp} /></div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8, alignItems: 'flex-end' }}>
              <div style={{ flex: 2, minWidth: 140 }}><label style={lbl}>Categoria</label>
                <select value={nova.categoria} onChange={e => setNova({ ...nova, categoria: e.target.value, subcategoria: '' })} style={inp}><option value="">—</option>{cats.map(c => <option key={c.nome} value={c.nome}>{c.nome}</option>)}</select>
              </div>
              {subs.length > 0 && <div style={{ flex: 2, minWidth: 140 }}><label style={lbl}>Subcategoria</label>
                <select value={nova.subcategoria} onChange={setN('subcategoria')} style={inp}><option value="">—</option>{subs.map(s => <option key={s} value={s}>{s}</option>)}</select>
              </div>}
              <div style={{ flex: 1, minWidth: 110 }}><label style={lbl}>Forma</label>
                <select value={nova.forma_pagamento} onChange={setN('forma_pagamento')} style={inp}><option value="">—</option>{formas.map(x => <option key={x} value={x}>{x}</option>)}</select>
              </div>
              <button onClick={lancar} disabled={salvando} style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 4, padding: '0 18px', height: 30, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: salvando ? 0.6 : 1 }}>{salvando ? '...' : 'Lançar'}</button>
            </div>
            {nova.valor_total && nova.num_parcelas > 1 && (
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>{nova.num_parcelas}x de aprox. {moeda((parseFloat(String(nova.valor_total).replace(/\./g, '').replace(',', '.')) || 0) / nova.num_parcelas)}</div>
            )}
          </>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0a1628' }}>Contas a {rec ? 'receber' : 'pagar'} deste {rotuloEntidade}</div>
        <div style={{ fontSize: 12, color: '#6b7280' }}>Em aberto: <b style={{ color: rec ? '#15803d' : '#b91c1c' }}>{moeda(totalAberto)}</b></div>
      </div>
      {contas.length === 0 ? (
        <div style={{ color: '#6b7280', fontSize: 13, padding: '8px 0' }}>Nenhuma conta lançada ainda.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ background: '#0a1628' }}>{(rec ? ['Descrição', 'Categoria', 'Consultor', 'Vencimento', 'Valor', 'Status', ''] : ['Descrição', 'Categoria', 'Vencimento', 'Valor', 'Status', '']).map((h, i) => <th key={i} style={{ padding: '5px 10px', color: '#fff', fontWeight: 600, textAlign: h === 'Valor' ? 'right' : 'left', fontSize: 11 }}>{h}</th>)}</tr></thead>
          <tbody>
            {contas.map((it, i) => (
              <tr key={it.id} style={{ background: it.status === 'quitado' ? '#f3f6f4' : (i % 2 === 0 ? '#fff' : '#fafafa'), borderBottom: '1px solid #e8eaed' }}>
                <td style={{ padding: '4px 10px' }}>{it.descricao}</td>
                <td style={{ padding: '4px 10px', color: '#6b7280' }}>{it.categoria || '—'}{it.subcategoria ? ` › ${it.subcategoria}` : ''}</td>
                {rec && (
                  <td style={{ padding: '4px 10px', whiteSpace: 'nowrap' }}>
                    {ehAdmin && it.grupo_parcelas ? (
                      <button onClick={() => setTrocaConsultor({ grupo: it.grupo_parcelas, atual: it.consultor || '' })}
                        title="Trocar o consultor desta venda (reflete em comissões, relatórios e PDF do pedido)"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12.5, color: '#1d4ed8', fontWeight: 600, padding: 0, textDecoration: 'underline dotted' }}>
                        {it.consultor || '— definir —'}
                      </button>
                    ) : (
                      <span style={{ fontSize: 12.5, color: it.consultor ? '#374151' : '#9ca3af' }}>{it.consultor || '—'}</span>
                    )}
                  </td>
                )}
                <td style={{ padding: '4px 10px', whiteSpace: 'nowrap' }}>
                  {ehAdmin ? (
                    <input type="date" key={it.id + it.vencimento} defaultValue={(it.vencimento || '').slice(0, 10)}
                      onChange={e => editarVencimento(it, e.target.value)}
                      style={{ ...inp, width: 132, height: 24 }} title="Alterar o vencimento desta parcela (reflete no Financeiro)" />
                  ) : fmtData(it.vencimento)}
                </td>
                <td style={{ padding: '4px 10px', textAlign: 'right' }}>
                  {ehAdmin ? (
                    <input key={it.id + it.valor} defaultValue={(it.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      onChange={e => { e.target.value = mascaraMoeda(e.target.value); }}
                      onBlur={e => editarValor(it, e.target.value)} inputMode="numeric"
                      style={{ ...inp, width: 90, height: 24, textAlign: 'right', fontWeight: 600 }} title="Editar valor (reflete no Financeiro)" />
                  ) : (
                    <span style={{ fontWeight: 600 }}>{moeda(it.valor)}</span>
                  )}
                </td>
                <td style={{ padding: '4px 10px' }}>
                  {ehAdmin ? (
                    <button onClick={() => setBaixa(it)}
                      title={it.status === 'quitado' ? 'Ver a baixa (ou estornar)' : `Dar baixa: registrar o ${rec ? 'recebimento' : 'pagamento'} sem abrir o Financeiro`}
                      style={{ fontSize: 11, fontWeight: 700, borderRadius: 4, padding: '3px 10px', cursor: 'pointer', border: '1px solid', whiteSpace: 'nowrap', ...(it.status === 'quitado' ? { background: '#f0fdf4', color: '#15803d', borderColor: '#bbf7d0' } : { background: '#16a34a', color: '#fff', borderColor: '#16a34a' }) }}>
                      {it.status === 'quitado' ? (rec ? '✓ Recebido' : '✓ Pago') : (rec ? '💰 Dar baixa' : '💰 Dar baixa')}
                    </button>
                  ) : (
                    <span style={{ fontSize: 10, fontWeight: 600, borderRadius: 3, padding: '2px 6px', ...(it.status === 'quitado' ? { background: '#f0fdf4', color: '#15803d' } : { background: '#fef9c3', color: '#854d0e' }) }}>
                      {it.status === 'quitado' ? (rec ? '✓ Recebido' : '✓ Pago') : 'Em aberto'}
                    </span>
                  )}
                </td>
                <td style={{ padding: '4px 10px', whiteSpace: 'nowrap' }}>
                  {rec && it.grupo_parcelas && (
                    <button onClick={() => baixarPedido(it)} disabled={gerandoPdf === it.grupo_parcelas}
                      title="Relatório do pedido em PDF (itens + parcelas) pra enviar ao cliente"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, opacity: gerandoPdf === it.grupo_parcelas ? .4 : 1 }}>
                      📄
                    </button>
                  )}
                  {ehAdmin && rec && it.grupo_parcelas && (
                    <button onClick={() => setEditarVenda(it.grupo_parcelas)}
                      title="Editar a venda: itens, quantidades e valores (com reajuste das parcelas em aberto)"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}>✏️</button>
                  )}
                  {ehAdmin && <button onClick={() => excluir(it)} title="Excluir" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}>🗑️</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>Cada parcela é uma conta: aparece também na tela Financeiro › {telaAlvo}, e alterar o valor aqui reflete lá (e vice-versa).</div>
    </div>
  );
}

// Modal de baixa (pagamento/recebimento) com parcial + saldo — igual ao da tela Financeiro
// Edição do pedido: itens da venda (serviço/qtde/valor unitário) com opção
// de reajustar as parcelas EM ABERTO pelo novo total — parcela paga não mexe
function ModalEditarVenda({ grupo, catalogo, formas, onFechar, onSalvo }) {
  const [itens, setItens] = useState(null); // null = carregando
  const [parcelas, setParcelas] = useState([]);
  const [forma, setForma] = useState('');
  const [reajustar, setReajustar] = useState(true);
  // Parcelamento editável (13/08): as ABERTAS viram linhas editáveis
  // (valor/vencimento), com controle de quantidade e cascata de valores
  const [abertasEdit, setAbertasEdit] = useState([]); // [{valor: 'str', vencimento}]
  const [dirtyParcelas, setDirtyParcelas] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const parseM = (v) => parseFloat(String(v || '').replace(/\./g, '').replace(',', '.')) || 0;

  useEffect(() => {
    apiGet(`financeiro/pedido/${encodeURIComponent(grupo)}`)
      .then(d => {
        if (d.erro) { setErro(d.erro); setItens([]); return; }
        setItens((d.itens?.length ? d.itens : [{ servico: '', quantidade: 1, valor_unitario: 0 }])
          .map(it => ({ servico: it.servico, quantidade: it.quantidade, valor: (it.valor_unitario || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) })));
        setParcelas(d.parcelas || []);
        setForma(d.forma_pagamento || '');
        setAbertasEdit((d.parcelas || []).filter(pp => pp.status !== 'quitado')
          .map(pp => ({ valor: (pp.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), vencimento: (pp.vencimento || '').slice(0, 10) })));
      })
      .catch(() => { setErro('Erro ao carregar o pedido.'); setItens([]); });
  }, [grupo]);

  const setItem = (i, k, v) => setItens(arr => arr.map((it, j) => j === i ? { ...it, [k]: v } : it));
  const novoTotal = (itens || []).reduce((s, it) => s + Math.max(1, parseInt(it.quantidade, 10) || 1) * parseM(it.valor), 0);
  const pago = parcelas.filter(p => p.status === 'quitado').reduce((s, p) => s + p.valor, 0);
  const abertas = parcelas.filter(p => p.status !== 'quitado').length;
  const previa = abertas > 0 ? (novoTotal - pago) / abertas : 0;
  const pagas = parcelas.filter(p => p.status === 'quitado');
  const restante = Math.round((novoTotal - pago) * 100) / 100;
  const somaAbertas = Math.round(abertasEdit.reduce((s, a) => s + parseM(a.valor), 0) * 100) / 100;
  const bateSoma = Math.abs(somaAbertas - restante) <= 0.05;

  const addMes = (iso, n) => { const d = new Date((iso || new Date().toISOString().slice(0, 10)) + 'T12:00:00'); d.setMonth(d.getMonth() + n); return d.toISOString().slice(0, 10); };
  const fmtBR = (v) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Regenera N parcelas iguais (datas mensais a partir da 1ª aberta atual)
  function regerarParcelas(n) {
    const qtd = Math.min(24, Math.max(restante > 0.005 ? 1 : 0, parseInt(n, 10) || 1));
    const base = abertasEdit[0]?.vencimento || addMes(null, 1);
    const cada = Math.floor((restante / qtd) * 100) / 100;
    const novas = Array.from({ length: qtd }, (_, i) => ({
      valor: fmtBR(i === qtd - 1 ? Math.round((restante - cada * (qtd - 1)) * 100) / 100 : cada),
      vencimento: addMes(base, i),
    }));
    setAbertasEdit(novas); setDirtyParcelas(true);
  }

  // Cascata: mexeu no valor da parcela i → as SEGUINTES dividem o que sobrou
  function setValorParcela(i, valorStr) {
    setDirtyParcelas(true);
    setAbertasEdit(arr => {
      const nova = arr.map((a, k) => k === i ? { ...a, valor: valorStr } : { ...a });
      const fixado = nova.slice(0, i + 1).reduce((s, a) => s + parseM(a.valor), 0);
      const seguintes = nova.length - (i + 1);
      if (seguintes > 0) {
        const sobra = Math.round((restante - fixado) * 100) / 100;
        const cada = Math.floor((sobra / seguintes) * 100) / 100;
        for (let k = i + 1; k < nova.length; k++) {
          const v = k === nova.length - 1 ? Math.round((sobra - cada * (seguintes - 1)) * 100) / 100 : cada;
          nova[k].valor = fmtBR(Math.max(0, v));
        }
      }
      return nova;
    });
  }

  async function salvar() {
    const validos = (itens || []).filter(it => it.servico && parseM(it.valor) > 0);
    if (!validos.length) return alert('A venda precisa de pelo menos um item com serviço e valor.');
    setSalvando(true);
    try {
      if (dirtyParcelas && restante > 0.005 && !bateSoma) {
        setSalvando(false);
        return alert(`As parcelas em aberto somam ${moeda(somaAbertas)}, mas o que falta receber é ${moeda(restante)} — use o ↻ redistribuir ou ajuste os valores.`);
      }
      const r = await apiSend(`financeiro/pedido/${encodeURIComponent(grupo)}`, 'PUT', {
        itens: validos.map(it => ({ servico: it.servico, quantidade: it.quantidade, valor_unitario: parseM(it.valor) })),
        forma_pagamento: forma,
        reajustar,
        ...(dirtyParcelas ? { parcelas_abertas: abertasEdit.map(a => ({ valor: parseM(a.valor), vencimento: a.vencimento })) } : {}),
      });
      if (r.erro) throw new Error(r.erro);
      alert(r.parcelas_ajustadas
        ? `Venda atualizada! ${r.parcelas_ajustadas} parcela(s) em aberto reajustada(s) pelo novo total.`
        : 'Venda atualizada!');
      onSalvo();
    } catch (e) { alert(e.message); }
    setSalvando(false);
  }

  return (
    <div onClick={onFechar} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, width: 640, maxWidth: '96vw', maxHeight: '90vh', overflowY: 'auto', padding: 18 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#0a1628', marginBottom: 12 }}>✏️ Editar venda</div>
        {erro && <div style={{ background: '#fef2f2', color: '#b91c1c', borderRadius: 6, padding: '8px 12px', fontSize: 13, marginBottom: 10 }}>{erro}</div>}
        {itens === null ? (
          <div style={{ color: '#6b7280', fontSize: 13 }}>Carregando o pedido…</div>
        ) : (
          <>
            {itens.map((it, i) => (
              <div key={i} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 6, alignItems: 'flex-end' }}>
                <div style={{ flex: 3, minWidth: 180 }}>
                  <label style={lbl}>Serviço</label>
                  <select value={it.servico} onChange={e => setItem(i, 'servico', e.target.value)} style={inp}>
                    <option value="">— escolha o serviço —</option>
                    {/* serviço da venda antiga que saiu do catálogo continua selecionável */}
                    {!catalogo.includes(it.servico) && it.servico && <option value={it.servico}>{it.servico}</option>}
                    {catalogo.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div style={{ width: 70 }}>
                  <label style={lbl}>Qtd</label>
                  <input type="number" min={1} value={it.quantidade} onChange={e => setItem(i, 'quantidade', Math.max(1, parseInt(e.target.value, 10) || 1))} style={inp} />
                </div>
                <div style={{ flex: 1, minWidth: 110 }}>
                  <label style={lbl}>Valor unitário (R$)</label>
                  <input value={it.valor} onChange={e => setItem(i, 'valor', mascaraMoeda(e.target.value))} inputMode="numeric" placeholder="0,00" style={inp} />
                </div>
                {itens.length > 1 && (
                  <button onClick={() => setItens(arr => arr.filter((_, k) => k !== i))} title="Remover item"
                    style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: 4, height: 30, padding: '0 10px', cursor: 'pointer', fontWeight: 700 }}>✕</button>
                )}
              </div>
            ))}
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 10 }}>
              <button onClick={() => setItens(arr => [...arr, { servico: '', quantidade: 1, valor: '' }])}
                style={{ background: '#fff', color: '#0a1628', border: '1px dashed #94a3b8', borderRadius: 4, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                ➕ Adicionar item
              </button>
              <div style={{ minWidth: 160 }}>
                <label style={lbl}>Forma de pagamento</label>
                <select value={forma} onChange={e => setForma(e.target.value)} style={{ ...inp, height: 30 }}>
                  <option value="">—</option>
                  {/* forma antiga fora do catálogo continua selecionável */}
                  {forma && !(formas || []).includes(forma) && <option value={forma}>{forma}</option>}
                  {(formas || []).map(x => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
            </div>

            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '10px 12px', fontSize: 13, marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                <span>Novo total: <b>{moeda(novoTotal)}</b></span>
                <span style={{ color: '#6b7280', fontSize: 12 }}>Já pago: {moeda(pago)}</span>
                <span style={{ color: '#6b7280', fontSize: 12 }}>Falta receber: <b style={{ color: '#0a1628' }}>{moeda(restante)}</b></span>
              </div>

              {pagas.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {pagas.map((pp, i) => (
                    <div key={pp.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: '#15803d', padding: '2px 0' }}>
                      ✓ parcela paga {i + 1} · {moeda(pp.valor)} · {(pp.data_pagamento || pp.vencimento || '').slice(0, 10).split('-').reverse().join('/')}
                    </div>
                  ))}
                </div>
              )}

              {restante > 0.005 ? (
                <div style={{ marginTop: 8 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>Parcelas em aberto:</span>
                    <select value={abertasEdit.length} onChange={e => regerarParcelas(e.target.value)} title="Trocar a quantidade redistribui o que falta em parcelas iguais (datas mensais)"
                      style={{ ...inp, width: 64, height: 26 }}>
                      {Array.from({ length: 24 }, (_, k) => k + 1).map(n => <option key={n} value={n}>{n}x</option>)}
                    </select>
                    <button type="button" onClick={() => regerarParcelas(abertasEdit.length)} title="Redistribuir o que falta igualmente entre as parcelas"
                      style={{ background: '#fff', border: '1px dashed #94a3b8', borderRadius: 4, padding: '3px 10px', fontSize: 11.5, cursor: 'pointer', color: '#0a1628' }}>↻ redistribuir igual</button>
                    <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: bateSoma ? '#15803d' : '#b91c1c' }}>
                      soma {moeda(somaAbertas)} {bateSoma ? '✓' : `≠ ${moeda(restante)}`}
                    </span>
                  </div>
                  {abertasEdit.map((a, i) => (
                    <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ fontSize: 11.5, color: '#6b7280', width: 64 }}>parcela {pagas.length + i + 1}</span>
                      <input value={a.valor} onChange={e => setValorParcela(i, mascaraMoeda(e.target.value))} inputMode="numeric"
                        title="Mexeu aqui, as parcelas seguintes se reajustam sozinhas (cascata)"
                        style={{ ...inp, width: 110, height: 26, textAlign: 'right', fontWeight: 600 }} />
                      <input type="date" value={a.vencimento} onChange={e => { const v = e.target.value; setDirtyParcelas(true); setAbertasEdit(arr => arr.map((x, k) => k === i ? { ...x, vencimento: v } : x)); }}
                        style={{ ...inp, width: 140, height: 26 }} />
                      {abertasEdit.length > 1 && (
                        <button type="button" onClick={() => { setDirtyParcelas(true); setAbertasEdit(arr => arr.filter((_, k) => k !== i)); }} title="Remover esta parcela (a soma precisa fechar — use ↻ pra redistribuir)"
                          style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: 4, height: 26, padding: '0 8px', cursor: 'pointer', fontWeight: 700 }}>✕</button>
                      )}
                    </div>
                  ))}
                  <div style={{ fontSize: 10.5, color: '#9ca3af' }}>Troque o valor de uma parcela e as seguintes se reajustam em cascata; a soma tem que fechar com o que falta receber.</div>
                </div>
              ) : (
                <div style={{ color: '#b45309', fontSize: 12, marginTop: 6 }}>Nada em aberto — o pedido está integralmente pago (mude os itens pra gerar diferença a cobrar).</div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={onFechar} style={{ background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 16px', fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
              <button onClick={salvar} disabled={salvando}
                style={{ background: '#0a1628', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: salvando ? .6 : 1 }}>
                {salvando ? 'Salvando…' : 'Salvar alterações'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Troca de consultor da venda — só o Vagner vê o botão que abre este modal.
// A troca reflete em cascata: comissões, relatórios de vendas e PDF do pedido
// leem o registro de venda (grupo_parcelas), então muda tudo junto.
function ModalConsultor({ grupo, atual, onFechar, onSalvo }) {
  const [usuarios, setUsuarios] = useState(null); // null = carregando
  const [escolhido, setEscolhido] = useState(atual || '');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    apiGet('usuarios')
      .then(d => setUsuarios((Array.isArray(d) ? d : []).map(u => u.nome).filter(Boolean)))
      .catch(() => setUsuarios([]));
  }, []);

  async function salvar() {
    if (!escolhido.trim()) return alert('Escolha o consultor.');
    if (escolhido.trim() === (atual || '').trim()) return onFechar();
    setSalvando(true);
    try {
      const r = await apiSend(`financeiro/pedido/${encodeURIComponent(grupo)}/consultor`, 'PUT', { consultor: escolhido.trim() });
      if (r.erro) throw new Error(r.erro);
      alert(`Consultor da venda alterado: ${r.de} → ${r.para}. Comissões e relatórios já refletem a troca.`);
      onSalvo();
    } catch (e) { alert(e.message); }
    setSalvando(false);
  }

  return (
    <div onClick={onFechar} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, width: 400, maxWidth: '94vw', padding: 18 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#0a1628', marginBottom: 4 }}>👤 Trocar consultor da venda</div>
        <p style={{ fontSize: 12.5, color: '#6b7280', margin: '0 0 12px' }}>
          Consultor atual: <b>{atual || '— sem consultor —'}</b>. A troca muda em cascata tudo que referencia esta venda: comissão, relatórios de vendas e o PDF do pedido.
        </p>
        {usuarios === null ? (
          <div style={{ color: '#6b7280', fontSize: 13 }}>Carregando a equipe…</div>
        ) : (
          <select value={escolhido} onChange={e => setEscolhido(e.target.value)} style={{ ...inp, marginBottom: 14 }}>
            <option value="">— escolha o consultor —</option>
            {atual && !usuarios.includes(atual) && <option value={atual}>{atual}</option>}
            {usuarios.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onFechar} style={{ background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 16px', fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
          <button onClick={salvar} disabled={salvando || usuarios === null}
            style={{ background: '#0a1628', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: salvando ? .6 : 1 }}>
            {salvando ? 'Salvando…' : 'Trocar consultor'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalBaixa({ item, formas, onFechar, onSalvo }) {
  const rec = item.tipo === 'receber';
  const jaQuitado = item.status === 'quitado';
  const total = item.valor_original != null ? item.valor_original : item.valor;
  const hoje = new Date(Date.now() - 3 * 3600 * 1000).toISOString().slice(0, 10);
  const [valorPago, setValorPago] = useState((jaQuitado ? item.valor : total).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const [forma, setForma] = useState(item.pago_forma || item.forma_pagamento || '');
  const [data, setData] = useState(item.data_pagamento ? item.data_pagamento.slice(0, 10) : hoje);
  const [gerar, setGerar] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const numPago = parseFloat(String(valorPago).replace(/\./g, '').replace(',', '.')) || 0;
  const saldo = Math.round((total - numPago) * 100) / 100;

  const confirmar = async () => {
    if (numPago <= 0) return alert('Informe o valor.');
    setSalvando(true);
    await apiSend(`financeiro/${item.id}/baixa`, 'PUT', { valor_pago: valorPago, forma, data_pagamento: data, gerar_remanescente: saldo > 0 && gerar });
    setSalvando(false); onSalvo();
  };
  const estornar = async () => {
    if (!window.confirm('Estornar? A conta volta para "em aberto".')) return;
    setSalvando(true); await apiSend(`financeiro/${item.id}/status`, 'PUT', { status: 'aberto' }); setSalvando(false); onSalvo();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }} onClick={onFechar}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 24, width: 420, maxWidth: '92%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0a1628', margin: '0 0 4px' }}>{jaQuitado ? '🧾 Baixa do ' + (rec ? 'recebimento' : 'pagamento') : (rec ? '💰 Registrar recebimento' : '💰 Registrar pagamento')}</h2>
        <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px' }}>{item.descricao} · total {moeda(total)}</p>
        {jaQuitado ? (
          <div>
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 14 }}>
              <Linha rotulo={rec ? 'Recebido em' : 'Pago em'} valor={fmtData(item.data_pagamento)} />
              <Linha rotulo="Valor" valor={moeda(item.valor)} destaque />
              {item.valor_original != null && item.valor_original !== item.valor && <Linha rotulo="Valor original" valor={`${moeda(item.valor_original)} (saldo de ${moeda(item.valor_original - item.valor)} gerado)`} />}
              <Linha rotulo="Forma" valor={item.pago_forma || '—'} />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', marginTop: 18 }}>
              <button onClick={estornar} disabled={salvando} style={{ ...inp, width: 'auto', cursor: 'pointer', color: '#b45309', borderColor: '#fde68a' }}>↺ Estornar</button>
              <button onClick={onFechar} style={{ background: '#0a1628', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Fechar</button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div><label style={lbl}>Valor {rec ? 'recebido' : 'pago'}</label><input value={valorPago} onChange={e => setValorPago(mascaraMoeda(e.target.value))} inputMode="numeric" style={inp} /></div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}><label style={lbl}>Forma</label><select value={forma} onChange={e => setForma(e.target.value)} style={inp}><option value="">—</option>{(formas || []).map(x => <option key={x} value={x}>{x}</option>)}</select></div>
              <div style={{ flex: 1 }}><label style={lbl}>Data</label><input type="date" value={data} onChange={e => setData(e.target.value)} style={inp} /></div>
            </div>
            {saldo > 0 && (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 13, color: '#b45309', fontWeight: 600 }}>{rec ? 'Recebimento' : 'Pagamento'} a menor · saldo de {moeda(saldo)}</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#374151', marginTop: 4, cursor: 'pointer' }}>
                  <input type="checkbox" checked={gerar} onChange={e => setGerar(e.target.checked)} /> Gerar nova conta a {rec ? 'receber' : 'pagar'} com o saldo remanescente
                </label>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 6 }}>
              <button onClick={onFechar} style={{ ...inp, width: 'auto', cursor: 'pointer', color: '#374151' }}>Cancelar</button>
              <button onClick={confirmar} disabled={salvando} style={{ background: rec ? '#16a34a' : '#dc2626', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: salvando ? 0.6 : 1 }}>{salvando ? '...' : 'Confirmar'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
function Linha({ rotulo, valor, destaque }) {
  return <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '2px 0' }}><span style={{ color: '#6b7280' }}>{rotulo}</span><span style={{ fontWeight: destaque ? 700 : 500, color: '#0a1628' }}>{valor}</span></div>;
}
